import type { ReactElement, FC } from "react";
import type { IconName } from "@similarweb/icons";
import { PlainTooltip } from "@similarweb/ui-components";
import { SWReactIcons } from "@similarweb/icons";
import type {
    AssetName,
    IAssetsContainerProps,
    IPredefinedAssetTemplate,
} from "app/pages/digital-suite/home-page/components/CustomAssets/types";
import { GenericAssetTemplate } from "app/pages/digital-suite/home-page/components/CustomAssets/Assets/GenericTemplate/GenericAssetTemplate";
import { PredefinedAssetTemplate } from "app/pages/digital-suite/home-page/components/CustomAssets/Assets/PredefinedTemplate/PredefinedAssetTemplate";
import {
    GridsContainer,
    GridsTable,
    TableWrapper,
} from "app/pages/digital-suite/home-page/components/CustomAssets/styledComponents";
import { AssetIcon } from "app/pages/digital-suite/home-page/components/CustomAssets/AssetIcon";
import {
    Header,
    IconContainer,
    TextContainer,
    Title,
    Subtitle,
    InfoWrapper,
} from "app/pages/digital-suite/home-page/components/CustomAssets/Containers/EmptyStateContainer/styledComponents";

interface IEmptyStateContainerProps extends IAssetsContainerProps<IPredefinedAssetTemplate> {
    type: AssetName;
    title: string;
    subtitle?: string;
    infoTooltipContent?: ReactElement;
    marginBottom?: number;
    genericTemplateProps?: {
        text: string;
        iconName: IconName;
        onClick: () => void;
    };
}

export const EmptyStateContainer: FC<IEmptyStateContainerProps> = ({
    columns,
    assets,
    type,
    title,
    subtitle,
    infoTooltipContent,
    genericTemplateProps,
    marginBottom,
}) => {
    const allocatedTemplateGrids = columns - (genericTemplateProps ? 1 : 0);
    const singleRowTemplates = assets?.slice(0, allocatedTemplateGrids);

    /**
     * Calculate the 'grid-template-column' property of the assets' container.
     * This method generates a grids template that stretches the assets over the first row of the table.
     *
     * @returns {string} A 'grid-template-column' value for the assets' container.
     */
    const calcColumnGridsTemplate = (): string => {
        const EmptyGridFr = genericTemplateProps ? "1fr" : "";
        const fraction = allocatedTemplateGrids / singleRowTemplates.length;
        return singleRowTemplates?.reduce((acc) => `${fraction}fr ${acc}`, EmptyGridFr) ?? EmptyGridFr;
    };

    return (
        <TableWrapper
            data-automation={`my-assets-${type}-empty-state`}
            marginBottom={marginBottom}
        >
            <GridsContainer>
                <Header>
                    <IconContainer>
                        <AssetIcon type={type} />
                    </IconContainer>
                    <TextContainer>
                        <Title data-automation={"my-assets-empty-state-title"}>{title}</Title>
                        {subtitle ? <Subtitle>{subtitle}</Subtitle> : null}
                    </TextContainer>
                    {infoTooltipContent ? (
                        <InfoWrapper>
                            <PlainTooltip
                                tooltipContent={infoTooltipContent}
                                placement={"top"}
                                closeDelay={100}
                                allowHover
                            >
                                <div>
                                    <SWReactIcons
                                        iconName={"info"}
                                        size={"xs"}
                                    />
                                </div>
                            </PlainTooltip>
                        </InfoWrapper>
                    ) : null}
                </Header>
                <GridsTable
                    columns={columns}
                    gridTemplateColumns={calcColumnGridsTemplate()}
                    gridsGap={8}
                >
                    {singleRowTemplates?.map((suggestion, index) => (
                        <PredefinedAssetTemplate
                            key={index}
                            {...suggestion}
                        />
                    ))}
                    {genericTemplateProps ? (
                        <GenericAssetTemplate
                            text={genericTemplateProps.text}
                            iconName={genericTemplateProps.iconName}
                            onClick={genericTemplateProps.onClick}
                        />
                    ) : null}
                </GridsTable>
            </GridsContainer>
        </TableWrapper>
    );
};
